/**
 * Embeddable ME Onsite registration widget loader (IIFE → widget.js).
 *
 * Usage (see buildWidgetEmbedCode for the full generated snippet):
 *   <link rel="preconnect" href="https://public.me-onsite.com" crossorigin>
 *   <div id="meo-widget" data-event-id="evt_…">
 *     <div class="meo-widget-placeholder" style="…">
 *       <p class="meo-widget-loading">Loading registration…</p>
 *       <a class="meo-widget-fallback" href="https://app.me-onsite.com/evt_…/register">Get tickets</a>
 *       <a class="meo-widget-fallback-manage" href="https://app.me-onsite.com/evt_…/register?view=manage">Already registered? Manage registration</a>
 *     </div>
 *   </div>
 *   <script src="https://embed.me-onsite.com/widget.js" async></script>
 *
 * Load path (MER-702): this file stays small and framework-free. On run it
 * attaches the Shadow Root (hiding the light-DOM placeholder), paints a
 * skeleton, starts GET /events/:eventId/widget on the public API, and injects
 * the content-hashed React app module (`chunks/app-*.js`). If the app cannot
 * run here (unsupported browser, blocked chunk, boot timeout) the host shows
 * a link to the hosted `/:eventId/register` page instead of staying blank.
 *
 * Fallback (MER-712): light-DOM children of the container stay visible only
 * until mount — attaching the Shadow Root (no <slot>) hides them. Keep it that
 * way so visitors whose browser blocks widget.js still get a link to the
 * hosted `/:eventId/register` page (which runs on our origin).
 *
 * Optional host theme overrides (set on the container; cross the open Shadow
 * Root). Unset vars fall back to the event appearance accent / Canvas surfaces:
 *   --meo-embed-accent              accent / register / check color
 *   --meo-embed-card-bg             card and modal background
 *   --meo-embed-card-border         card border
 *   --meo-embed-field-bg            input/select/textarea background
 *   --meo-embed-field-border        input/select/textarea border
 *   --meo-embed-row-bg              unselected ticket-option row background
 *   --meo-embed-row-border          unselected ticket-option row border
 *   --meo-embed-placeholder-height  space reserved before the card mounts
 *
 * Tracking (MER-578): host-page query params are captured from window.location
 * the same way as the public event page, e.g.
 *   ?source=email&campaign=emailsignature
 *   ?utm_source=newsletter&utm_medium=email
 * Dashboard live previews set data-meo-preview="true" so they are not counted.
 *
 * Deep link into manage / complete-payment (MER-612; payment reminders MER-611):
 *   ?view=manage&email=guest@example.com
 * Prefills GuestEmailCodeSignIn; OTP is still required. `view` / `email`
 * override data-meo-view / data-meo-guest-email on the host container.
 *
 * Typography (`font-family`, `color`, `line-height`) inherits from the host
 * page by default — do not set those on the widget unless you want to force them.
 *
 * No Clerk SDK — guest auth is email OTP via the public API.
 */
import { createWidgetLoader } from './loaderCore';

function resolveScriptSrc() {
  const current = document.currentScript;
  if (current?.src) {
    return current.src;
  }
  const scripts = document.querySelectorAll('script[src*="widget.js"]');
  return scripts.length ? scripts[scripts.length - 1].src : '';
}

// A second copy of widget.js on the page reuses the first runtime.
if (typeof window !== 'undefined' && typeof document !== 'undefined' && !window.MeoWidget) {
  const loader = createWidgetLoader({
    scriptSrc: resolveScriptSrc(),
    appEntry: __MEO_WIDGET_APP_ENTRY__,
    publicApiUrl: __MEO_PUBLIC_API_URL__,
    version: __MEO_WIDGET_VERSION__,
  });
  window.MeoWidget = loader.api;
  loader.boot();
}
